import React, { useMemo, useState } from 'react'
import { CAlert, CBadge, CButton, CCard, CCardBody, CSmartTable } from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import { cilReload, cilWarning } from '@coreui/icons'
import moment from 'moment'
import TableSearchToolbar from 'src/components/common/TableSearchToolbar'

const SEARCH_FIELDS = [
  { key: 'username', label: 'User' },
  { key: 'player_name', label: 'Player' },
]

const columns = [
  { key: 'username', label: 'User', filter: false, sorter: true },
  { key: 'player_name', label: 'Player', filter: false, sorter: true },
  { key: 'created_at', label: 'Date', filter: false, sorter: true, _style: { width: '20%' } },
]

const PowerUsedPlayer = ({ powerUsers = [], loading, error, onRetry }) => {
  const [searchField, setSearchField] = useState(SEARCH_FIELDS[0].key)
  const [searchValue, setSearchValue] = useState('')
  const [itemsPerPage, setItemsPerPage] = useState(10)

  // The endpoint hands back the whole history in one call, so searching, sorting and
  // paging all happen here rather than through another request.
  const items = useMemo(() => {
    const term = searchValue.trim().toLowerCase()
    if (!term) return powerUsers
    return powerUsers.filter((item) => (item[searchField] || '').toLowerCase().includes(term))
  }, [powerUsers, searchField, searchValue])

  const noItemsLabel = searchValue.trim()
    ? 'No usage matches your search'
    : 'No one has used this power yet'

  return (
    <CCard className="pc-panel">
      <CCardBody>
        <div className="pc-panel-toolbar">
          <div>
            <h2 className="pc-panel-title">
              Power Usage{' '}
              <CBadge color="secondary" shape="rounded-pill" className="align-middle">
                {powerUsers.length}
              </CBadge>
            </h2>
            <p className="pc-panel-note">
              Every time a member has played Triple Captain, and on which player.
            </p>
          </div>
        </div>

        {error ? (
          <CAlert color="danger" className="d-flex flex-wrap align-items-center gap-2 mb-0">
            <CIcon icon={cilWarning} className="flex-shrink-0" />
            <span>We couldn&apos;t load the power usage history.</span>
            <CButton
              color="danger"
              variant="outline"
              size="sm"
              className="ms-auto"
              onClick={onRetry}
            >
              <CIcon icon={cilReload} className="me-1" />
              Retry
            </CButton>
          </CAlert>
        ) : (
          <>
            <TableSearchToolbar
              fields={SEARCH_FIELDS}
              field={searchField}
              onFieldChange={(field) => {
                setSearchField(field)
                setSearchValue('')
              }}
              value={searchValue}
              onSearch={setSearchValue}
              placeholder="Search usage..."
            />

            <div className="ge-datatable ge-datatable--no-select">
              <CSmartTable
                columns={columns}
                items={items}
                itemsPerPage={itemsPerPage}
                itemsPerPageSelect
                loading={loading}
                noItemsLabel={noItemsLabel}
                pagination
                sorterValue={{ column: 'created_at', state: 'desc' }}
                scopedColumns={{
                  username: (item) => (
                    <td>
                      {item.username ? (
                        <span className="pc-cell-primary">{item.username}</span>
                      ) : (
                        <span className="pc-cell-muted">Member removed</span>
                      )}
                    </td>
                  ),
                  player_name: (item) => (
                    <td>
                      {item.player_name ? (
                        <span className="pc-cell-primary">{item.player_name}</span>
                      ) : (
                        <span className="pc-cell-muted">Player removed</span>
                      )}
                    </td>
                  ),
                  created_at: (item) => (
                    <td className="pc-cell-date">
                      {item.created_at ? moment(item.created_at).format('D MMM YYYY') : '-'}
                    </td>
                  ),
                }}
                tableProps={{ hover: true, responsive: true, className: 'align-middle mb-0' }}
                onItemsPerPageChange={setItemsPerPage}
              />
            </div>
          </>
        )}
      </CCardBody>
    </CCard>
  )
}

export default PowerUsedPlayer
